/**
 * One past call.
 *
 * The row has to answer five questions before anyone clicks it: what was it,
 * when, WHO was there, is the door still open, and is there anything to read
 * yet. All five are now in the list response.
 *
 * WHO used to be the one that was not. `RoomSummary` carried a participant
 * count and no people, so this row fetched a row's full detail on hover to put
 * names on the faces — a request per hovered row, for a fact the listing could
 * have carried. `SessionSummary.participants_preview` now carries it ("first
 * four distinct human identities in join order", with names), so the hover
 * fetch and its 180ms timer are DELETED. Nothing here waits on anything.
 *
 * Two fallbacks survive the deletion, and neither is the old workaround:
 *
 *   - a preview is EMPTY for a room the control plane has not answered about
 *     yet — every room for the first few seconds of its life, and any room
 *     whose session it has since forgotten. Then the creator's name, which is
 *     this app's own fact from SQLite, is the only name that exists.
 *   - `participant_count` is upstream's when upstream has answered and the
 *     local token count when it has not, and it can exceed the preview's four.
 *     The overflow chip counts from it, so a five-person call says "+2" rather
 *     than losing the fifth person to the preview's bound.
 *
 * ON A PHONE the row answers the same questions with a different shape,
 * because 343px does not hold five columns:
 *
 *   - the STATUS BADGE moves out of the right rail and onto the second line.
 *     Left where it is, it takes ~100px off the right of a 343px row and the
 *     title column comes out at 117px — which is a title truncated to two
 *     words on every row in the list. On the second line it costs nothing and
 *     the title gets 229px. This is the one thing that is rendered twice, and
 *     it is worth it: the desktop rail is what the operator has today.
 *   - the NAMES go. The avatars beside them already say who was there, and the
 *     names were the first thing to become an ellipsis.
 *   - the NODE goes. `rtc-01` is an operator's fact and it is still searchable
 *     and still on the session screen; it is not worth a phone row's width.
 *   - the DURATION stays in the right rail rather than joining the second
 *     line. Next to a time of day, `4:56` reads as a clock; in the rail, in
 *     the same position it holds on desktop, it reads as a length.
 *   - the RECORDING COUNT goes UNDER the duration, in the rail, at both
 *     widths. The second line is already the badge plus a time and was
 *     measured at 172 of the 192px it gets at 375px; the rail has a whole
 *     empty line under a single-line duration and the two facts are the same
 *     kind of fact — how much call there is. The rail stays shorter than the
 *     title column, so no row grows.
 */

import { Link } from '@tanstack/react-router'
import { ChevronRight, Ban, FileText, Hourglass, Mic, Radio, Video } from 'lucide-react'
import type { RoomSummary } from '@era/shared'
import { Badge, LiveDot } from '@/components/ui/badge'
// Shared with the session screen deliberately: the same person must wear the
// same monogram and the same colour on both, and `u_a` must not render as `U_`.
import { Monogram } from '@/features/session/components/monogram'
import { formatDateTime, formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import { peopleOf } from '../people'

export function SessionRow({ room }: { room: RoomSummary }) {
  const { people, total } = peopleOf(room)
  const shown = people.slice(0, 3)
  const extra = Math.max(0, total - shown.length)

  return (
    <Link
      to="/session/$sessionId"
      params={{ sessionId: room.session_id }}
      className={cn(
        // ~68px tall on a phone against the 44px minimum, and the whole row is
        // the target. `active:` is here because the shell turned the default
        // iOS tap highlight off — a list with no press feedback is the clearest
        // tell that a phone screen is a web page.
        'group flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-3 sm:gap-4 sm:px-5 sm:py-3.5',
        'shadow-[var(--shadow-card)] transition-[border-color,transform] duration-150',
        'hover:border-line-strong focus-visible:outline-none active:bg-surface-2',
      )}
    >
      {/* -- who ------------------------------------------------------------- */}
      {/* Stacked in JOIN ORDER with the first person on top, which is the order
          the preview arrives in and the same order the session screen's roster
          uses — so a face keeps its colour and its position across the two
          screens. */}
      {/* `min-w` on the cluster, not just `shrink-0`. A row with one avatar was
          50px narrower here than a row with an avatar plus a "+1" overflow
          chip, so every title in the list started at a different x and the
          column read as ragged. The floor is the two-chip width, which covers
          the overwhelmingly common 1-and-2-person rows. */}
      <div className="flex min-w-[3.125rem] shrink-0 -space-x-1.5">
        {shown.map((person, index) => (
          <Monogram
            key={person.key}
            displayName={person.name}
            index={index}
            size="sm"
            className="ring-2 ring-surface"
            style={{ zIndex: 4 - index }}
          />
        ))}
        {extra > 0 ? (
          <span className="flex size-7 items-center justify-center rounded-full bg-surface-2 text-[0.625rem] font-semibold text-ink-muted ring-2 ring-surface">
            +{extra}
          </span>
        ) : null}
      </div>

      {/* -- what ------------------------------------------------------------ */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[0.9375rem] font-medium text-ink">
          {room.title ?? 'Untitled call'}
        </p>
        {/* `flex-nowrap` below `sm`: this line is now the badge plus a time,
            and if it is allowed to wrap, one row in the list is 22px taller
            than its neighbours for no reason a reader can see. It fits. */}
        <p className="mt-0.5 flex flex-nowrap items-center gap-x-2 text-xs text-ink-muted sm:flex-wrap sm:gap-y-0.5">
          <StatusBadge room={room} className="shrink-0 sm:hidden" />
          {/* Two names and a count, not four names: at 640px the third name is
              already an ellipsis, and "+2" is the fact the ellipsis was hiding.
              The `title` carries the rest, which is the whole preview and
              therefore free. */}
          <span className="hidden truncate sm:inline" title={people.map((p) => p.name).join(', ')}>
            {people
              .slice(0, 2)
              .map((person) => person.name)
              .join(', ')}
            {total > 2 ? ` +${total - 2}` : ''}
          </span>
          <span aria-hidden className="hidden text-ink-dim sm:inline">
            ·
          </span>
          {/* `truncate`, not `shrink-0`. The badge beside it is the one thing
              here that must never be squeezed; if a locale's time format plus
              the widest badge ("not recorded") ever exceeds the 192px this
              column gets at 375px, this degrades to an ellipsis instead of
              overhanging the duration in the rail. Measured at 172 of 192
              today — it fits, and this is what happens when it stops. */}
          <span className="truncate">
            {formatDateTime(room.created_at).split(', ').slice(-1)[0]}
          </span>
          {room.node_name ? (
            <>
              <span aria-hidden className="hidden text-ink-dim sm:inline">
                ·
              </span>
              <span className="ident hidden text-[0.6875rem] text-ink-dim sm:inline">
                {room.node_name}
              </span>
            </>
          ) : null}
        </p>
      </div>

      {/* -- state ------------------------------------------------------------ */}
      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        <span className="flex flex-col items-end leading-none">
          <span className="text-xs tabular text-ink-muted sm:text-sm">
            {formatDuration(room.duration_ms)}
          </span>
          {/* One line, up to two facts about what this call kept.
              It is a row rather than two stacked lines because the rail must
              stay shorter than the title column — a third line here is a taller
              row for every call that kept video, and the list's even rhythm is
              most of what makes it scannable. */}
          {room.recording_count > 0 || room.video_recording_ever_enabled || room.video_ready ? (
            <span className="mt-1 inline-flex items-center gap-1.5">
              {/* `recording_count` is per-SPEAKER files, not "was this recorded" —
                  a two-person call has two. It is drawn only when there is at
                  least one, because a zero here is ambiguous between "recording
                  was off" (the badge already says that) and "the pipeline has not
                  written them yet", and neither is worth a permanent 0 in the
                  rail. */}
              {room.recording_count > 0 ? (
                <span
                  className="inline-flex items-center gap-1 text-[0.625rem] tabular text-ink-dim"
                  title={`${room.recording_count} per-speaker ${
                    room.recording_count === 1 ? 'recording' : 'recordings'
                  }`}
                >
                  <Mic className="size-2.5" aria-hidden />
                  {room.recording_count}
                </span>
              ) : null}

              {/* `video_ready` is the verified/playable fact copied from the
                  existing session-list refresh. `ever_enabled` remains the
                  truthful fallback when capture was requested but rendering
                  has not produced a playable file. Neither needs another
                  detail request. */}
              {room.video_recording_ever_enabled || room.video_ready ? (
                <span
                  className={`inline-flex items-center text-[0.625rem] ${
                    room.video_ready ? 'text-accent' : 'text-ink-dim'
                  }`}
                  title={
                    room.video_ready
                      ? 'The retained-video file is ready to play.'
                      : 'This call retained video, but no playable file is confirmed yet.'
                  }
                >
                  <Video className="size-2.5" aria-hidden />
                  <span className="sr-only">
                    {room.video_ready ? 'retained video ready' : 'retained video requested'}
                  </span>
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
        <StatusBadge room={room} className="hidden sm:inline-flex" />
        <ChevronRight className="size-4 text-ink-dim transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  )
}

/**
 * One badge, and it says the most useful true thing.
 *
 * The order matters: "still open" beats everything, because a room whose code
 * still works is the only state on this screen a user can act on. Then the
 * permanent absences, then the wait. Never an error tone for "not yet" — a
 * transcript that has not arrived is a healthy session in its normal first
 * five minutes.
 *
 * `room.status === 'open'` is upstream's `joinable`, copied by the BFF, not
 * this app's reading of `session_state`. That derivation was FINDINGS.md #19
 * and it badged six of thirteen rooms "open" after everyone had hung up.
 */
function StatusBadge({ room, className }: { room: RoomSummary; className?: string }) {
  if (room.status === 'open') {
    return (
      <Badge tone="live" className={className}>
        <LiveDot />
        open
      </Badge>
    )
  }
  if (!room.recording_ever_enabled) {
    return (
      <Badge tone="neutral" className={className}>
        <Ban className="size-3" />
        not recorded
      </Badge>
    )
  }
  if (room.has_transcript) {
    return (
      <Badge tone="ok" className={className}>
        <FileText className="size-3" />
        transcript
      </Badge>
    )
  }
  if (room.session_state === 'failed') {
    return (
      <Badge tone="critical" className={className}>
        failed
      </Badge>
    )
  }
  if (room.session_state === 'purged') {
    return (
      <Badge tone="neutral" className={className}>
        purged
      </Badge>
    )
  }
  return (
    <Badge tone="neutral" className={className}>
      {room.session_state === 'live' ? <Radio className="size-3" /> : <Hourglass className="size-3" />}
      processing
    </Badge>
  )
}
